/**
 * @fileoverview The line under a PileCard's pictures: when they were taken, how
 * many there are, and the group's own ⋯ menu.
 *
 * The ⋯ removes all of the group (the page asks first) and, on a seed packets
 * group, offers "Ask about it as a plant" (the reader misread it; the same POST
 * /tray/not-packets). Split out of PileCard to keep that file under 800 lines
 * (CLAUDE.md rule 5, B107); the look and behaviour are unchanged.
 *
 * Where: PileCard, under the PhotoStrip.
 * API: none; the page's callbacks arrive as props.
 * Words (en/es): tray.pile.undated, photos, photosOne, groupMenu, askAsPlant,
 * removePhoto, removeGroup.
 *
 * @module PileHeader
 */

import React from 'react';
import { MoreHorizontal } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { DateTime } from '@/shared/utils/dateUtils';
import type { TrayPhoto } from '@/shared/api/tray';

/** What the header needs to say when, how many, and to remove the group. */
interface PileHeaderProps {
  /** The group's photos, as drawn. */
  photos: TrayPhoto[];
  /** The day the server gave the group; null or absent when it has none. */
  day?: string | null;
  /** True while one of the pile's answers is being saved. */
  busy: boolean;
  /** True for a seed packets group, whose ⋯ offers "Ask about it as a plant". */
  isPackets: boolean;
  /** "Ask about it as a plant": the reader misread it. */
  onNotPackets?: () => void;
  /** Asks to remove the whole group: the page asks first. */
  onRemoveGroup: () => void;
}

/**
 * The group's day and time, its photo count, and its ⋯ menu.
 *
 * @param props - PileHeaderProps.
 */
export const PileHeader: React.FC<PileHeaderProps> = ({ photos, day = null, busy, isPackets, onNotPackets, onRemoveGroup }) => {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  // When: the day (the server's), and the time of the first photograph.
  const at = photos[0]?.taken_at ? DateTime.fromISO(photos[0].taken_at) : null;
  const when = [day ?? t('tray.pile.undated'), at?.isValid ? at.setLocale(language).toLocaleString(DateTime.TIME_SIMPLE) : null]
    .filter(Boolean).join(', ');
  const count = photos.length === 1 ? t('tray.pile.photosOne') : t('tray.pile.photos', { count: photos.length });
  return (
    <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
      <span>{when}</span>
      <span className="flex items-center gap-1">
        {count}
        {/* The group's own ⋯: removing all of it, which asks first. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t('tray.pile.groupMenu')}
              disabled={busy}
              className="-my-2 -mr-2 flex h-9 w-9 items-center justify-center rounded-full text-foreground hover:bg-muted"
            >
              <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {/* The reader misread it: plants, not packets (the same POST /tray/not-packets). */}
            {isPackets && onNotPackets && (
              <DropdownMenuItem onSelect={onNotPackets}>{t('tray.pile.askAsPlant')}</DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={onRemoveGroup} className="text-destructive">
              {photos.length === 1 ? t('tray.pile.removePhoto') : t('tray.pile.removeGroup', { count: photos.length })}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </span>
    </div>
  );
};
